import React, { useState } from 'react';
import { DropDownMenu, Flex, Text, Icon, Input, Button, ButtonSelect, Select } from '@upsales/components';
import { PrimaryButton } from '@upsales/components/Buttons';
import type {
	Widget,
	WidgetDataSourceProps,
	WidgetRow,
	EditWidgetRowProps,
	DataSource
} from 'App/components/hooks/integrations/externalIntegrationTypes';
import { UiWidgetDropdownOption } from './UiWidgetDropdownOption';
import { useTranslation } from 'Components/Helpers/translate';
import WidgetFieldSetting from './helpers/WidgetFieldSetting';
import { useExternalIntegrationSelector } from '../hooks/integrations/ExternalIntegrationModal/ExternalIntegrationContext';
import UiWidget from './UiWidget';
import UiWidgetModal from './UiWidgetModal';
import BemClass from '@upsales/components/Utils/bemClass';
import './UiWidgetDropdown.scss';
import { get } from 'lodash';
import ClickSetting from './helpers/ClickSetting';
import { removeBracketsFromTag } from './UiWidgetHelpers';

export const EditUiWidgetDropdown = ({ row, onUpdate, dataSources }: EditWidgetRowProps) => {
	const [children, setChildren] = useState<WidgetRow[]>(row.settings?.children || []);
	const { t } = useTranslation();
	const text = row.settings?.text || '';
	const optionsType = row.settings?.optionsType || 'dataSource';
	const dataSourceOptions = Object.keys(dataSources || {}).map(name => ({
		id: name,
		title: name
	}));
	const selectedDataSource = dataSourceOptions.find(option => option.id === row.settings?.dataSource) || null;

	const handleAddOption = () => {
		const newChildren = [
			...children,
			{ type: 'DropdownOption', settings: { label: '' }, sortOrder: children.length + 1 }
		];
		setChildren(newChildren);
		if (onUpdate) {
			onUpdate({
				...row,
				settings: {
					...row.settings,
					children: newChildren
				}
			});
		}
	};

	const handleChange = (field: string, value: string) => {
		const changingToDataSource = field === 'optionsType' && value === 'dataSource';
		const changingToOptions = field === 'optionsType' && value === 'options';

		const updatedRow = {
			...row,
			settings: {
				...row.settings,
				[field]: value
			}
		};

		if (changingToDataSource) {
			updatedRow.settings.children = [];
		}
		if (changingToOptions) {
			updatedRow.settings.dataSource = '';
			updatedRow.settings.click = '';
			updatedRow.settings.clickSettings = undefined;
		}

		onUpdate(updatedRow);
	};

	const handleClickSettingChange = (clickAction: string, clickSettings: Record<string, any>) => {
		const updatedOption = {
			...row,
			settings: {
				...row.settings,
				click: clickAction,
				clickSettings
			}
		};
		onUpdate(updatedOption);
	};

	return (
		<Flex direction="column" gap="u2" className="EditUiWidgetDropdown">
			<WidgetFieldSetting text={t('default.text')}>
				<Input value={text} onChange={e => handleChange('text', e.target.value)} />
			</WidgetFieldSetting>

			<ButtonSelect
				value={optionsType}
				options={[
					{ value: 'dataSource', title: t('default.dataSource') },
					{ value: 'options', title: t('default.options') }
				]}
				onChange={value => handleChange('optionsType', value)}
			/>

			{optionsType === 'options' ? (
				<PrimaryButton size="sm" onClick={handleAddOption}>
					<Flex gap="u1" alignItems="center">
						<Icon name="plus" />
						{t('default.addOption')}
					</Flex>
				</PrimaryButton>
			) : null}

			{optionsType === 'dataSource' ? (
				<>
					<WidgetFieldSetting text={t('default.dataSource')}>
						<Select
							value={selectedDataSource}
							options={dataSourceOptions}
							onChange={option => handleChange('dataSource', option.id)}
							anchor=".Modals"
						/>
					</WidgetFieldSetting>
					<WidgetFieldSetting text={t('default.optionsTag')}>
						<Input
							value={row.settings?.optionsTag}
							onChange={e => handleChange('optionsTag', e.target.value)}
						/>
					</WidgetFieldSetting>

					<WidgetFieldSetting text={t('default.optionTextTag')}>
						<Input
							value={row.settings?.optionTextTag}
							onChange={e => handleChange('optionTextTag', e.target.value)}
						/>
					</WidgetFieldSetting>

					<ClickSetting
						row={row}
						onChange={(clickAction, clickSettings) => handleClickSettingChange(clickAction, clickSettings)}
					/>
				</>
			) : null}
		</Flex>
	);
};

const UiWidgetDropdown: React.FC<WidgetDataSourceProps> = ({ row, widgetExternalId, dataSources }) => {
	const classes = new BemClass('UiWidgetDropdown');
	const widgets = useExternalIntegrationSelector(state => state.widgets);
	const [widget, setWidget] = useState<Widget | null>(null);
	const [modalWidget, setModalWidget] = useState<Widget | null>(null);

	const children: WidgetRow[] = row.settings?.children || [];
	const text = row.settings?.text || '';
	const optionsType = row.settings?.optionsType || 'dataSource';
	const optionsTag = row.settings?.optionsTag || '';
	const optionTextTag = row.settings?.optionTextTag || '';
	const click = row.settings?.click || '';
	const clickSettings = row.settings?.clickSettings || {};

	const handleClick = (clickAction: string, clickSettings: Record<string, any>) => {
		if (!clickSettings) {
			return;
		}

		if (clickAction === 'Widget') {
			setWidget(widgets?.find(widget => widget.id === clickSettings.widget) || null);
		} else if (clickAction === 'Modal') {
			const foundWidget = widgets?.find(widget => `${widget.id}` === `${clickSettings.widget}`);
			if (foundWidget) {
				if (clickSettings.optionId && foundWidget.settings?.dataSource) {
					for (const [key, dataSource] of Object.entries<DataSource>(foundWidget.settings.dataSource)) {
						if (dataSource.requestPath.includes('{{optionId}}')) {
							foundWidget.settings.dataSource[key].requestPath = dataSource.requestPath.replace(
								'{{optionId}}',
								clickSettings.optionId
							);
						}
					}
				}
				setModalWidget(foundWidget);
			}
		}
	};

	const getOptionTextKey = (optionTextTag: string, index: number) => {
		let optionTextKey = optionTextTag ?? '';
		const optionsKey = removeBracketsFromTag(row.settings?.optionsTag || '');
		optionTextKey = optionTextKey.replace(optionsKey, '');
		optionTextKey = optionTextKey.replace(/(\.|^)\d+/, (match: string) => match.replace(/\.\d+/, `${index}`));
		return removeBracketsFromTag(optionTextKey);
	};

	const getOptionsFromDataSource = () => {
		if (!dataSources) return [];
		const optionsKey = removeBracketsFromTag(optionsTag);
		const options = get(dataSources, optionsKey, []);
		return options?.map?.((option, index) => {
			const optionTextKeyForIndex = getOptionTextKey(optionTextTag, index);
			let optionValue = get(options, optionTextKeyForIndex, '');
			if (typeof optionValue !== 'string') {
				optionValue = '';
			}
			const optionId = get(option, 'id', '');

			return {
				settings: { text: optionValue, click, clickSettings: { ...clickSettings, optionId } }
			};
		});
	};

	const options: any = optionsType === 'options' ? children : getOptionsFromDataSource();

	return (
		<>
			<DropDownMenu
				renderTrigger={(_, setExpanded) => (
					<Button className={classes.elem('trigger').b()} onClick={setExpanded} type="lined">
						<Text>{text}</Text>
					</Button>
				)}
				align="left"
				arrowed
			>
				{closeMenu => (
					<Flex direction="column">
						{options?.map((option: any, idx: number) => (
							<UiWidgetDropdownOption
								key={option.id || idx}
								row={option}
								widgetExternalId={widgetExternalId}
								onClick={(clickAction, clickSettings) => {
									closeMenu();
									handleClick(clickAction, clickSettings);
								}}
							/>
						))}
					</Flex>
				)}
			</DropDownMenu>
			{widget ? <UiWidget widget={widget} /> : null}
			{modalWidget ? <UiWidgetModal widget={modalWidget} onClose={() => setModalWidget(null)} /> : null}
		</>
	);
};

export default UiWidgetDropdown;
